import React, { useEffect, useReducer, useState } from 'react';
import { EmployeeSettings } from 'page/EmployeeSettings';
import GeneralSettings from './GeneralSettings';
import EmployeeNumberSettings from './EmployeeNumberSettings';
import TabGroup from '@paycor/picl/tabs';
import style from './style.module.scss';
import {
	getHasNewEmployeeSettingsPageEnabled,
	getHasEmployeeNumberSettingsEnabled,
	loadCompanyDetails,
	loadEmployeeSettings,
	loadHasBSwiftService,
	loadHasPendingHiresWithEmployeeNumber
} from 'services/employeeService';
import Banner from '@paycor/picl/banner';
import { employeeSettingsActions, employeeSettingsReducer } from 'reducers/employeeSettingsReducer';
import { LoadingStatus } from '@paycor/pacl';
import config from 'config';
import GlobalNav from 'components/GlobalNav';
import { useParams } from 'react-router-dom';
import { t } from 'i18next';
import UnsavedChangesModal from 'page/UnsavedChangesModal/UnsavedChangesModal';

const {
	HOST: { SECURE_BASE_DOMAIN_URL }
} = config;

const initialState = {
	settings: {},
	savedSettings: {},
	errors: {}
};

const EmployeeSettingsTabbedView = () => {
	const { companyId, clientId } = useParams();

	const [isLoading, setIsLoading] = useState(false);
	const [pageFailed, setPageFailed] = useState(false);
	const [hasEmployeeNumberSettingsEnabled, setHasEmployeeNumberSettingsEnabled] = useState(null);
	const [bannerMessage, setBannerMessage] = useState({
		showBanner: false,
		text: '',
		status: null
	});
	const [_employeeSettings, dispatchEmployeeSettings] = useReducer(employeeSettingsReducer, initialState);
	const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
	const [isDirty, setIsDirty] = useState(false);

	useEffect(() => {
		const handleBeforeUnload = (event) => {
			if (isDirty) {
				event.preventDefault();
				event.returnValue = '';
			}
		};
		window.addEventListener('beforeunload', handleBeforeUnload);

		return () => {
			window.removeEventListener('beforeunload', handleBeforeUnload);
		};
	}, [isDirty]);

	useEffect(() => {
		const initializeAppEffect = async () => {
			try {
				const hasNewEmployeeSettingsPageEnabled = await getHasNewEmployeeSettingsPageEnabled(companyId);
				const hasEmployeeNumberSettingsEnabled = await getHasEmployeeNumberSettingsEnabled(companyId);
				setHasEmployeeNumberSettingsEnabled(hasEmployeeNumberSettingsEnabled);
				if (hasNewEmployeeSettingsPageEnabled) {
					const [companyDetails, employeeSettings, hasPendingHiresWithEmployeeNumber, hasBSwiftService] = await Promise.all([
						loadCompanyDetails(companyId, clientId),
						loadEmployeeSettings(companyId),
						loadHasPendingHiresWithEmployeeNumber(companyId),
						loadHasBSwiftService(companyId, clientId)
					]);

					dispatchEmployeeSettings({
						type: employeeSettingsActions.INITIALIZE,
						payload: {
							value: {
								...employeeSettings,
								employeeNumberSeed: employeeSettings.employeeNumberSeed?.toString(),
								hasPendingHiresWithEmployeeNumber: hasPendingHiresWithEmployeeNumber ?? false,
								hasBSwiftService: hasBSwiftService ?? false,
								companyName: `${companyDetails.companyId} - ${companyDetails.companyName}`
							}
						}
					});
				} else {
					window.location.href = `${SECURE_BASE_DOMAIN_URL}/Perform/Company#${clientId}/HrSettings?useLegacyHrSettings=true`;
				}
			} catch (error) {
				console.log(error);
				setBannerMessage({
					showBanner: true,
					text: t(`Unable_to_load_employee_settings`, `Sorry, we can’t load Employee Settings right now. Please wait a bit and try again.`),
					status: Banner.Status.ERROR
				});
				setPageFailed(true);
			} finally {
				setIsLoading(false);
			}
		};
		initializeAppEffect();
	}, [companyId, clientId]);

	function onCancelNavigation() {
		setShowUnsavedChangesModal(false);
	}

	async function onSaveAndContinue() {
		setShowUnsavedChangesModal(false);
	}

	const handleIsDirtyChange = (value) => {
		setIsDirty(value);
	};

	useEffect(
		() => {
			if (bannerMessage.status !== Banner.Status.SUCCESS) return;
			const timer = setTimeout(() => {
				setBannerMessage({
					showBanner: false,
					text: '',
					status: Banner.Status.SUCCESS
				});
			}, 5000);

			return () => {
				clearTimeout(timer);
			};
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[bannerMessage.showBanner]
	);

	const handleTabChange = () => {
		if (isDirty) {
			setShowUnsavedChangesModal(true);
			return false;
		}
		return true;
	};

	const renderBanner = () => {
		return (
			<div className={bannerMessage.showBanner ? style.banner_wrapper : ''}>
				<Banner
					id={'banner'}
					message={bannerMessage.showBanner ? bannerMessage.text : null}
					status={bannerMessage.status ? bannerMessage.status : Banner.Status.SUCCESS}
					show={bannerMessage.showBanner}
					onClose={() => {
						setBannerMessage({ ...bannerMessage, showBanner: !bannerMessage.showBanner });
					}}
					onToggle={() => {
						setBannerMessage({ ...bannerMessage, showBanner: !bannerMessage.showBanner });
					}}
				/>
			</div>
		);
	};

	const renderEmployeeHeader = () => {
		return (
			<header className={style.header_container}>
				<h1 className={style.header_title}>{t('header_tittle', 'Employee Settings')}</h1>
				<span className={style.client_header}>{_employeeSettings.settings.companyName}</span>
			</header>
		);
	};
	return (
		<>
			{hasEmployeeNumberSettingsEnabled === true ? (
				<div>
					{isLoading && <LoadingStatus />}
					<GlobalNav companyId={companyId} clientId={clientId} />
					{pageFailed ? (
						<div className={style.hrsetting_container}>
							{renderEmployeeHeader()}
							{renderBanner()}
						</div>
					) : (
						<div className={style.hrsetting_container}>
							{renderEmployeeHeader()}
							{renderBanner()}


							<TabGroup onNavigateCheck={handleTabChange}>
								<TabGroup.Tab id="general" title={t('General_Settings', 'General Settings')} key={1}>
									<GeneralSettings onIsDirtyChange={handleIsDirtyChange} />
								</TabGroup.Tab>

								<TabGroup.Tab id="employee" title={t('Employee_Number_Settings', 'Employee Number Settings')} key={1}>
									<EmployeeNumberSettings onIsDirtyChange={handleIsDirtyChange} />
								</TabGroup.Tab>
							</TabGroup>
						</div>
					)}
				</div>
			) : (
				<EmployeeSettings />
			)}
			<div>
				<UnsavedChangesModal onCancelNavigation={onCancelNavigation} onSaveAndContinue={onSaveAndContinue} showUnsavedChangesModal={showUnsavedChangesModal} translate={t} />
			</div>
		</>
	);
};

export default EmployeeSettingsTabbedView;
